import { useEntity } from '@backstage/plugin-catalog-react';

import { InfoCard, Link } from '@backstage/core-components';
import { Button, Chip, IconButton, Grid, Tooltip, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import type {
  CriticalityType,
  DataPropertiesType,
  InformationCategoriesType,
  SecurityClassificationType,
  ExposureType,
  Entity,
  RecoveryObjective,
} from '@zeiss/backstage-plugin-security-asset-classification-common';

import {
  RECOVERY_OBJECTIVE_LABELS,
} from '@zeiss/backstage-plugin-security-asset-classification-common';

import { generateSearchQueryRepoUrlPicker } from '@zeiss/backstage-plugin-recommendations';

export type SecurityAssetClassificationType = {
  criticality?: CriticalityType;
  dataProperties?: DataPropertiesType[];
  informationCategories?: InformationCategoriesType[];
  securityClassification?: SecurityClassificationType[];
  exposure?: ExposureType;
};

export type BusinessImpactAnalysisType = {
  rto?: RecoveryObjective;
  rpo?: RecoveryObjective;
  nextReview?: string;
};

export const isSecurityAssetClassificationAvailable = (entity: Entity) =>
  Boolean(
    (entity.kind === 'System' ||
      entity.kind === 'API' ||
      entity.kind === 'Component') &&
      entity.spec?.type !== 'documentation',
  );

const splitClassificationValue = <T extends string>(
  value?: string,
): T[] | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return value ? (value.split('|') as T[]) : [];
};

export const getSecurityAssetClassifications = (
  entity: Entity,
): SecurityAssetClassificationType => ({
  criticality: entity.spec?.zeiss?.security?.classification?.criticality,
  dataProperties: splitClassificationValue<DataPropertiesType>(
    entity.spec?.zeiss?.security?.classification?.['data-properties'],
  ),
  informationCategories: splitClassificationValue<InformationCategoriesType>(
    entity.spec?.zeiss?.security?.classification?.['information-categories'],
  ),
  securityClassification: splitClassificationValue<SecurityClassificationType>(
    entity.spec?.zeiss?.security?.classification?.['security-classification'],
  ),
  exposure: entity.spec?.zeiss?.security?.classification?.exposure,
});

export const getBusinessImpactAnalysis = (
  entity: Entity,
): BusinessImpactAnalysisType => ({
  rto: entity.spec?.zeiss?.security?.['business-impact-analysis']?.['recovery-time-objective'],
  rpo: entity.spec?.zeiss?.security?.['business-impact-analysis']?.['recovery-point-objective'],
  nextReview: entity.spec?.zeiss?.security?.['business-impact-analysis']?.nextReview,
});

export const determineCriticalityColor = (criticality: CriticalityType) => {
  switch (criticality) {
    case 'Low':
      return '#ffff54';
    case 'Medium':
      return '#f5c242';
    case 'High':
      return '#ea3423';
    default:
      return '#b02518';
  }
};

const formatDateForLocale = (date?: string): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsedDate = new Date(`${date}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(parsedDate);
};

const ClassificationField = ({
  label,
  values,
  xs = 6,
  tooltip,
  isCriticality = false,
}: {
  label: string;
  values?: string[] | string;
  xs?: 4 | 6;
  tooltip?: string;
  isCriticality?: boolean;
}) => {
  const isEmpty =
    values === undefined ||
    (Array.isArray(values) && values.length === 0);

  const labelElement = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="h6">{label}</Typography>

      {tooltip && (
        <Tooltip title={tooltip}>
          <IconButton
            aria-label={`Information about ${label}`}
            size="small"
            style={{ marginLeft: 4, padding: 2 }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  if (isEmpty) {
    return (
      <Grid item xs={xs}>
        {labelElement}
        <Typography variant="body2" color="textSecondary">
          -
        </Typography>
      </Grid>
    );
  }

  if (typeof values === 'string') {
    return (
      <Grid item xs={xs}>
        {labelElement}
        <Chip
          label={values}
          size={isCriticality ? 'medium' : 'small'}
          style={
            isCriticality
              ? {
                  backgroundColor: determineCriticalityColor(
                    values as CriticalityType,
                  ),
                  fontWeight: 'bold',
                }
              : undefined
          }
        />
      </Grid>
    );
  }

  return (
    <Grid item xs={xs}>
      {labelElement}
      {values.map(value => (
        <Chip key={value} label={value} size="small" />
      ))}
    </Grid>
  );
};

const MissingClassification = ({
  title,
  message,
  templateName,
  buttonLabel,
  searchQueryRepoUrlPicker,
}: {
  title: string;
  message: string;
  templateName: string;
  buttonLabel: string;
  searchQueryRepoUrlPicker: string;
}) => (
  <Grid item xs={12}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>

    <Typography variant="body1" paragraph>
      {message}
    </Typography>

    <Link
      to={`/create/templates/default/${templateName}${searchQueryRepoUrlPicker}`}
    >
      <Button color="primary" variant="contained">
        {buttonLabel}
      </Button>
    </Link>
  </Grid>
);

export const SecurityAssetClassification = () => {
  const { entity } = useEntity();

  const securityAssetClassifications = getSecurityAssetClassifications(
    entity as Entity,
  );

  const isSystem = entity.kind === 'System';

  const bia = getBusinessImpactAnalysis(entity as Entity);

  const sourceLocation =
    entity.metadata?.annotations?.['backstage.io/source-location'] || '';
  const searchQueryRepoUrlPicker = sourceLocation.startsWith('url:')
    ? generateSearchQueryRepoUrlPicker(sourceLocation)
    : '';

  const {
    criticality,
    dataProperties,
    informationCategories,
    securityClassification,
    exposure,
  } = securityAssetClassifications;

  const missingContent = isSystem
    ? {
        title: 'Missing or incomplete Business Impact Analysis',
        message: 'Please create or complete the Business Impact Analysis for this system.',
        templateName: 'business-impact-analysis',
        buttonLabel: 'CREATE BIA',
      }
    : {
        title: 'Missing or incomplete classification',
        message:
          'Please classify this component with the security asset classification template.',
        templateName: 'v2component-security-asset-classification',
        buttonLabel: 'CREATE Classification',
      };

  const isClassificationComplete = Object.values(
    securityAssetClassifications,
  ).every(value => value !== undefined);

  const isBiaComplete =
    bia.rto !== undefined &&
    bia.rpo !== undefined &&
    bia.nextReview !== undefined;

  const shouldShowClassification = !isSystem || isClassificationComplete;

  if (!isSystem && !isClassificationComplete) {
    return (
      <InfoCard
        title={isSystem ? 'Business Impact Analysis' : 'Security Asset Classification'}
        variant="fullHeight"
      >
        <Grid container spacing={3}>
          <MissingClassification
            {...missingContent}
            searchQueryRepoUrlPicker={searchQueryRepoUrlPicker}
          />
        </Grid>
      </InfoCard>
    );
  }

  return (
    <InfoCard title={
      isSystem ? 'Business Impact Analysis' : 'Security Asset Classification'
    } variant="fullHeight">
      <Grid container spacing={3}>
        {shouldShowClassification && (
          <>
            <ClassificationField
              label="Criticality"
              values={criticality}
              isCriticality
            />
            <ClassificationField
              label="Data Properties"
              values={dataProperties}
            />
            <ClassificationField
              label="Information Categories"
              values={informationCategories}
            />
            <ClassificationField
              label="Security Classification"
              values={securityClassification}
            />
            <ClassificationField label="Exposure" values={exposure} />
          </>
        )}

        {isSystem && isBiaComplete && (
          <Grid item xs={12}>
            <Grid container spacing={1} style={{ maxWidth: 800 }}>
              <ClassificationField
                label="RTO"
                values={bia.rto ? RECOVERY_OBJECTIVE_LABELS[bia.rto] : undefined}
                tooltip="Recovery Time Objective"
                xs={4}
              />
              <ClassificationField
                label="RPO"
                values={bia.rpo ? RECOVERY_OBJECTIVE_LABELS[bia.rpo] : undefined}
                tooltip="Recovery Point Objective"
                xs={4}
              />
              <ClassificationField
                label="Next Review"
                values={formatDateForLocale(bia.nextReview)}
                xs={4}
              />
            </Grid>
          </Grid>
        )}

        {isSystem && !isBiaComplete && (
          <MissingClassification
            title="Missing or incomplete Business Impact Analysis"
            message="Please create or complete the Business Impact Analysis for this system."
            templateName="business-impact-analysis"
            buttonLabel="CREATE BIA"
            searchQueryRepoUrlPicker={searchQueryRepoUrlPicker}
          />
        )}
      </Grid>
    </InfoCard>
  );
};
